import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { MdDiscount } from 'react-icons/md';
import { FaExclamationTriangle, FaPlus, FaTimes, FaChevronRight, FaChevronDown, FaRedo, FaBan, FaCopy, FaEnvelope } from 'react-icons/fa';
import saApi, {
  createClub, checkClubSlug,
  createClubInvitation, listClubInvitations, revokeClubInvitation, resendClubInvitation,
} from '../../services/superAdminApi';
import useEscapeKey from '../../hooks/useEscapeKey';
import { getStatusBadge } from '../../utils/clubStatus';
import { getPlans } from '../../services/subscriptionService';

// Deduped status filter options (trial + trialing collapse to one 'Trial' option).
const STATUS_FILTER_OPTIONS = [
  { value: '',              label: 'All statuses'  },
  { value: 'trial',         label: 'Trial'         },
  { value: 'active',        label: 'Active'        },
  { value: 'suspended',     label: 'Suspended'     },
  { value: 'trial_expired', label: 'Trial Expired' },
  { value: 'cancelled',     label: 'Cancelled'     },
  { value: 'archived',      label: 'Archived'      },
];

const PLAN_COLORS = {
  free: '#8a847c',
  pro:  '#a78bfa',
};

const RISK_COLORS = {
  high:   { bg: 'rgba(239,68,68,0.12)',   text: '#f87171', border: 'rgba(239,68,68,0.3)'   },
  medium: { bg: 'rgba(251,191,36,0.10)',  text: '#fbbf24', border: 'rgba(251,191,36,0.3)'  },
  low:    { bg: 'rgba(107,114,128,0.12)', text: '#8a847c', border: 'rgba(107,114,128,0.3)' },
};

const RISK_FILTER_OPTIONS = [
  { value: '',       label: 'All Risk Levels' },
  { value: 'high',   label: 'High Risk'       },
  { value: 'medium', label: 'Medium Risk'     },
  { value: 'low',    label: 'Low Risk'        },
  { value: 'none',   label: 'No Risk'         },
];

function TrialDaysChip({ days }) {
  if (days === null || days === undefined) return null;
  const urgent = days <= 3;
  const warn   = days <= 7;
  return (
    <span className="ml-1.5 text-xs font-semibold px-1.5 py-0.5 rounded"
      style={{ background: urgent ? '#7f1d1d' : warn ? '#78350f' : '#28261f', color: urgent ? '#fca5a5' : warn ? '#fcd34d' : '#8a847c' }}>
      {days}d
    </span>
  );
}

function RiskCell({ riskLevel, riskLabels }) {
  if (!riskLevel || !riskLabels?.length) {
    return <span className="text-xs" style={{ color: '#6b6560' }}>—</span>;
  }
  const topStyle = RISK_COLORS[riskLevel] || RISK_COLORS.low;
  return (
    <div className="flex flex-wrap gap-1">
      {riskLabels.slice(0, 2).map((r, i) => {
        const s = RISK_COLORS[r.level] || RISK_COLORS.low;
        return (
          <span key={i} className="text-xs font-semibold px-1.5 py-0.5 rounded"
            style={{ background: s.bg, color: s.text, border: `1px solid ${s.border}` }}>
            {r.label}
          </span>
        );
      })}
      {riskLabels.length > 2 && (
        <span className="text-xs font-semibold px-1.5 py-0.5 rounded"
          style={{ background: topStyle.bg, color: topStyle.text, border: `1px solid ${topStyle.border}` }}>
          +{riskLabels.length - 2}
        </span>
      )}
    </div>
  );
}

// Club types — the Club model enum, default taekwondo.
// Canonical Stage-1 verticals. These drive all vocabulary; keep in sync with the Club enum
// and utils/publicProfile.js vocab().
const CLUB_TYPE_OPTIONS = [
  { value: 'taekwondo', label: 'Taekwondo (Students)' },
  { value: 'gym',       label: 'Gym (Members)' },
  { value: 'yoga',      label: 'Yoga (Members · Instructors)' },
  { value: 'general',   label: 'General / Other (Members)' },
];

// Normalize a name to a slug (mirrors the backend's [a-z0-9-] rule).
const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

const MODAL_INPUT = {
  width: '100%', background: '#1a1918', border: '1px solid rgba(237,232,224,0.08)',
  borderRadius: 10, padding: '8px 12px', color: '#ede8e0', fontSize: 13, outline: 'none', colorScheme: 'dark',
};
const MODAL_LABEL = { display: 'block', fontSize: 11, fontWeight: 600, color: '#8a847c', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.05em' };

// Segmented toggle button for the create/invite mode switch.
function ModeTab({ active, onClick, children }) {
  return (
    <button type="button" onClick={onClick}
      className="flex-1 py-2 rounded-lg text-xs font-semibold transition-colors"
      style={{
        background: active ? '#c8102e' : 'transparent',
        color:      active ? '#fff' : '#8a847c',
      }}>
      {children}
    </button>
  );
}

// ── Add Club modal — "Create manually" or "Send invitation" ────────────────────
function CreateClubModal({ onClose, onCreated, onInvited }) {
  const [mode,          setMode]          = useState('create'); // 'create' | 'invite'
  const [name,          setName]          = useState('');
  const [slug,          setSlug]          = useState('');
  const [slugTouched,   setSlugTouched]   = useState(false);
  const [clubType,      setClubType]      = useState('taekwondo');
  const [ownerName,     setOwnerName]     = useState('');
  const [ownerEmail,    setOwnerEmail]    = useState('');
  const [ownerPhone,    setOwnerPhone]    = useState('');
  const [ownerPassword, setOwnerPassword] = useState('');
  const [plan,          setPlan]          = useState('free');
  const [submitting,    setSubmitting]    = useState(false);
  const [debouncedSlug, setDebouncedSlug] = useState('');
  const [inviteLink,    setInviteLink]    = useState(null); // set when email delivery failed

  useEscapeKey(onClose, !submitting);

  // Auto-generate the slug from the name until the user edits it manually.
  useEffect(() => {
    if (!slugTouched) setSlug(slugify(name));
  }, [name, slugTouched]);

  // Debounce the slug for the live availability check.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSlug(slug), 350);
    return () => clearTimeout(t);
  }, [slug]);

  const { data: slugData, isFetching: slugChecking } = useQuery({
    queryKey:  ['sa-check-slug', debouncedSlug],
    queryFn:   () => checkClubSlug(debouncedSlug).then(r => r.data),
    enabled:   debouncedSlug.trim().length >= 3,
    staleTime: 10_000,
  });

  let slugState = 'idle'; // idle | short | checking | available | taken
  if (slug.trim().length > 0 && slug.trim().length < 3) slugState = 'short';
  else if (slug.trim().length >= 3) {
    if (debouncedSlug !== slug || slugChecking || !slugData) slugState = 'checking';
    else slugState = slugData.available ? 'available' : 'taken';
  }

  // ── Field validation (mirrors the backend rules) ──────────────────────────────
  const emailValid = /^\S+@\S+\.\S+$/.test(ownerEmail.trim());
  // Nepal local (exactly 10 digits) OR international (+ then 7–14 digits).
  const phoneValid = /^\d{10}$/.test(ownerPhone) || /^\+\d{7,14}$/.test(ownerPhone);
  const passwordValid = ownerPassword.length >= 8;

  const emailErr    = ownerEmail.trim().length > 0 && !emailValid;
  const phoneErr    = ownerPhone.length > 0 && !phoneValid;
  const passwordErr = ownerPassword.length > 0 && !passwordValid;

  const baseValid = name.trim().length >= 3 && name.trim().length <= 60
    && slug.trim().length >= 3 && slugState !== 'taken' && emailValid;

  const canSubmit = !submitting && (
    mode === 'invite'
      ? baseValid
      : baseValid && ownerName.trim() && phoneValid && passwordValid
  );

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      if (mode === 'invite') {
        const res = await createClubInvitation({
          clubName: name.trim(),
          slug:     slug.trim(),
          clubType,
          plan,
          email:    ownerEmail.trim(),
        });
        if (res.data?.emailSent === false) {
          // Email delivery failed — surface the link so it can be shared manually.
          setInviteLink(res.data?.inviteUrl || null);
          toast.error('Invitation created — email failed to send. Copy the link below.', { duration: 8000 });
          onInvited?.({ keepOpen: true });
        } else {
          toast.success(res.data?.message || `Invitation sent to ${ownerEmail.trim()}`);
          onInvited?.();
        }
      } else {
        const res = await createClub({
          name:          name.trim(),
          slug:          slug.trim(),
          clubType,
          ownerName:     ownerName.trim(),
          ownerEmail:    ownerEmail.trim(),
          ownerPhone:    ownerPhone.trim(),
          ownerPassword,
          plan,
          country:       'Nepal',
        });
        if (res.data?.emailSent === false) {
          toast.error('Club created — welcome email failed, share the credentials manually.', { duration: 8000 });
        } else {
          toast.success(res.data?.message || 'Club created');
        }
        onCreated();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || `Failed to ${mode === 'invite' ? 'send invitation' : 'create club'}`);
    } finally {
      setSubmitting(false);
    }
  };

  const copyLink = () => {
    if (!inviteLink) return;
    navigator.clipboard?.writeText(inviteLink).then(
      () => toast.success('Link copied'),
      () => toast.error('Could not copy — select and copy manually'),
    );
  };

  const slugColor = slugState === 'available' ? '#34d399' : slugState === 'taken' ? '#f87171' : '#6b6560';
  const isInvite = mode === 'invite';

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
      onClick={() => !submitting && onClose()}>
      <div className="w-full max-w-lg rounded-2xl flex flex-col"
        style={{ background: '#1e1d1b', border: '1px solid rgba(237,232,224,0.08)', maxHeight: '90vh' }}
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'rgba(237,232,224,0.08)' }}>
          <h2 className="font-bold text-[#ede8e0] flex items-center gap-2"><FaPlus className="text-red-400" /> Add Club</h2>
          <button onClick={() => !submitting && onClose()} className="text-[#8a847c] hover:text-[#ede8e0]"><FaTimes /></button>
        </div>

        <div className="overflow-y-auto px-6 py-5 space-y-4">
          {/* Mode toggle */}
          <div className="flex gap-1 p-1 rounded-xl" style={{ background: '#1a1918', border: '1px solid rgba(237,232,224,0.08)' }}>
            <ModeTab active={mode === 'create'} onClick={() => { setMode('create'); setInviteLink(null); }}>Create manually</ModeTab>
            <ModeTab active={mode === 'invite'} onClick={() => { setMode('invite'); setInviteLink(null); }}>Send invitation</ModeTab>
          </div>
          <p className="text-xs" style={{ color: '#6b6560' }}>
            {isInvite
              ? 'The owner gets an email link to set their own password and finish setup.'
              : 'You set a temporary password; the owner is forced to change it on first login.'}
          </p>

          {/* Emailless invite fallback — copyable link */}
          {inviteLink && (
            <div className="rounded-xl p-3" style={{ background: '#78350f22', border: '1px solid #b45309' }}>
              <p className="text-xs font-semibold mb-1.5" style={{ color: '#fcd34d' }}>Email delivery failed — share this link:</p>
              <div className="flex items-center gap-2">
                <input readOnly value={inviteLink} onClick={e => e.target.select()}
                  style={{ ...MODAL_INPUT, fontSize: 11, fontFamily: 'monospace' }} />
                <button type="button" onClick={copyLink}
                  className="flex-shrink-0 px-3 py-2 rounded-lg text-xs font-semibold text-white flex items-center gap-1.5"
                  style={{ background: '#b45309' }}>
                  <FaCopy /> Copy
                </button>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label style={MODAL_LABEL}>Club Name</label>
              <input style={MODAL_INPUT} value={name} onChange={e => setName(e.target.value)}
                placeholder="e.g. Elite Taekwondo" maxLength={60} />
            </div>
            <div>
              <label style={MODAL_LABEL}>Club Type</label>
              <select style={MODAL_INPUT} value={clubType} onChange={e => setClubType(e.target.value)}>
                {CLUB_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label style={MODAL_LABEL}>Club URL (slug)</label>
            <div className="flex items-center gap-2">
              <input style={MODAL_INPUT} value={slug}
                onChange={e => { setSlugTouched(true); setSlug(slugify(e.target.value)); }}
                placeholder="elite-taekwondo" />
              <span className="text-xs whitespace-nowrap flex-shrink-0 text-right" style={{ minWidth: 88, color: slugColor }}>
                {slugState === 'checking'  ? 'checking…'
                  : slugState === 'available' ? '✓ available'
                  : slugState === 'taken'   ? '✗ taken'
                  : slugState === 'short'   ? 'min 3 chars' : ''}
              </span>
            </div>
            <p className="text-xs mt-1" style={{ color: '#6b6560' }}>{slug || 'your-club'}.trainaxis.com</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {!isInvite && (
              <div>
                <label style={MODAL_LABEL}>Owner Name</label>
                <input style={MODAL_INPUT} value={ownerName} onChange={e => setOwnerName(e.target.value)}
                  placeholder="Full name" />
              </div>
            )}
            <div className={isInvite ? 'col-span-2' : ''}>
              <label style={MODAL_LABEL}>Owner Email</label>
              <input
                style={{ ...MODAL_INPUT, borderColor: emailErr ? '#f87171' : 'rgba(237,232,224,0.08)' }}
                type="email" value={ownerEmail}
                onChange={e => setOwnerEmail(e.target.value.replace(/\s/g, ''))}
                placeholder="owner@club.com" />
              {emailErr && <p className="text-xs mt-1" style={{ color: '#f87171' }}>Enter a valid email address.</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {!isInvite && (
              <div>
                <label style={MODAL_LABEL}>Owner Phone</label>
                <input
                  style={{ ...MODAL_INPUT, borderColor: phoneErr ? '#f87171' : 'rgba(237,232,224,0.08)' }}
                  value={ownerPhone}
                  onChange={e => {
                    // Allow digits and a single leading + only, and cap the length while typing:
                    // "+" → up to 14 digits (international), otherwise up to 10 (Nepali local).
                    const hasPlus = e.target.value.trim().startsWith('+');
                    const digits = e.target.value.replace(/\D/g, '').slice(0, hasPlus ? 14 : 10);
                    setOwnerPhone((hasPlus ? '+' : '') + digits);
                  }}
                  placeholder="98XXXXXXXX or +9779800000000" />
                <p className="text-xs mt-1" style={{ color: phoneErr ? '#f87171' : '#6b6560' }}>
                  10-digit Nepali number or +country format
                </p>
              </div>
            )}
            <div className={isInvite ? 'col-span-2' : ''}>
              <label style={MODAL_LABEL}>Plan</label>
              <select style={MODAL_INPUT} value={plan} onChange={e => setPlan(e.target.value)}>
                <option value="free">Free (30 members)</option>
                <option value="pro">Pro (unlimited)</option>
              </select>
            </div>
          </div>

          {!isInvite && (
            <div>
              <label style={MODAL_LABEL}>Temporary Password</label>
              <input
                style={{ ...MODAL_INPUT, borderColor: passwordErr ? '#f87171' : 'rgba(237,232,224,0.08)' }}
                type="text" value={ownerPassword}
                onChange={e => setOwnerPassword(e.target.value)} placeholder="At least 8 characters" />
              <p className="text-xs mt-1" style={{ color: passwordErr ? '#f87171' : '#6b6560' }}>
                {passwordErr
                  ? `${8 - ownerPassword.length} more character${8 - ownerPassword.length === 1 ? '' : 's'} needed`
                  : 'Shown as plain text — the owner is forced to change it on first login.'}
              </p>
            </div>
          )}
        </div>

        <div className="flex gap-2 px-6 py-4 border-t" style={{ borderColor: 'rgba(237,232,224,0.08)' }}>
          <button type="button" onClick={() => !submitting && onClose()}
            className="flex-1 py-2.5 rounded-xl text-sm font-medium text-[#8a847c] hover:text-[#ede8e0] border transition-colors"
            style={{ borderColor: 'rgba(237,232,224,0.08)' }}>
            {inviteLink ? 'Done' : 'Cancel'}
          </button>
          <button type="button" onClick={handleSubmit} disabled={!canSubmit}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            style={{ background: '#c8102e' }}>
            {isInvite && !submitting && <FaEnvelope className="text-xs" />}
            {submitting
              ? (isInvite ? 'Sending…' : 'Creating…')
              : (isInvite ? 'Send Invitation' : 'Create Club')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Revoke-invitation confirm modal ────────────────────────────────────────────
function RevokeInviteModal({ invite, onCancel, onConfirm, busy }) {
  useEscapeKey(onCancel, !busy);
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
      onClick={() => !busy && onCancel()}>
      <div className="w-full max-w-sm rounded-2xl p-6"
        style={{ background: '#1e1d1b', border: '1px solid rgba(237,232,224,0.08)' }}
        onClick={e => e.stopPropagation()}>
        <h3 className="font-bold text-[#ede8e0] mb-2">Revoke invitation?</h3>
        <p className="text-sm mb-5" style={{ color: '#8a847c' }}>
          The link sent to <span className="text-[#ede8e0] font-medium">{invite.email}</span> for{' '}
          <span className="text-[#ede8e0] font-medium">{invite.clubName}</span> will stop working. This can't be undone.
        </p>
        <div className="flex gap-2">
          <button type="button" onClick={() => !busy && onCancel()}
            className="flex-1 py-2.5 rounded-xl text-sm font-medium text-[#8a847c] hover:text-[#ede8e0] border transition-colors"
            style={{ borderColor: 'rgba(237,232,224,0.08)' }}>Cancel</button>
          <button type="button" onClick={onConfirm} disabled={busy}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white transition-colors disabled:opacity-50"
            style={{ background: '#c8102e' }}>{busy ? 'Revoking…' : 'Revoke'}</button>
        </div>
      </div>
    </div>
  );
}

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '—');

// ── Pending invitations — collapsed section (like the "Written Off" pattern) ────
function PendingInvitations() {
  const qc = useQueryClient();
  const [open,     setOpen]     = useState(false);
  const [revoking, setRevoking] = useState(null); // invite pending confirmation
  const [busyId,   setBusyId]   = useState(null);

  const { data } = useQuery({
    queryKey: ['sa-club-invitations'],
    queryFn:  () => listClubInvitations().then(r => r.data.invitations),
  });

  const invitations = data || [];
  const pending = invitations.filter(i => i.status === 'pending');

  const refresh = () => qc.invalidateQueries({ queryKey: ['sa-club-invitations'] });

  const handleResend = async (inv) => {
    setBusyId(inv._id);
    try {
      const res = await resendClubInvitation(inv._id);
      if (res.data?.emailSent === false) {
        toast.error('Re-sent, but the email failed to deliver.', { duration: 7000 });
      } else {
        toast.success(`Invitation re-sent to ${inv.email}`);
      }
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to resend');
    } finally {
      setBusyId(null);
    }
  };

  const handleRevoke = async () => {
    if (!revoking) return;
    setBusyId(revoking._id);
    try {
      await revokeClubInvitation(revoking._id);
      toast.success('Invitation revoked');
      setRevoking(null);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to revoke');
    } finally {
      setBusyId(null);
    }
  };

  if (pending.length === 0) return null;

  return (
    <div className="rounded-xl border" style={{ borderColor: 'rgba(237,232,224,0.08)' }}>
      <button onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-sm">
        <span className="flex items-center gap-2 font-medium" style={{ color: '#c5bfb5' }}>
          {open ? <FaChevronDown className="text-xs" /> : <FaChevronRight className="text-xs" />}
          <FaEnvelope className="text-xs" style={{ color: '#60a5fa' }} />
          Pending Invitations
          <span className="px-2 py-0.5 rounded-full text-xs font-semibold"
            style={{ background: 'rgba(96,165,250,0.12)', color: '#60a5fa' }}>{pending.length}</span>
        </span>
        <span className="text-xs" style={{ color: '#6b6560' }}>{open ? 'Hide' : 'Show'}</span>
      </button>

      {open && (
        <div className="border-t overflow-x-auto" style={{ borderColor: 'rgba(237,232,224,0.08)' }}>
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr style={{ background: '#1a1918', borderBottom: '1px solid rgba(237,232,224,0.08)' }}>
                {['Email', 'Club', 'Sent', 'Expires', ''].map((h, i) => (
                  <th key={i} className="px-4 py-2.5 text-left font-medium text-xs" style={{ color: '#8a847c' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pending.map(inv => (
                <tr key={inv._id} style={{ borderBottom: '1px solid rgba(237,232,224,0.08)' }}>
                  <td className="px-4 py-3" style={{ color: '#c5bfb5' }}>{inv.email}</td>
                  <td className="px-4 py-3">
                    <div style={{ color: '#c5bfb5' }}>{inv.clubName}</div>
                    <div className="text-xs" style={{ color: '#6b6560' }}>{inv.slug}.trainaxis.com</div>
                  </td>
                  <td className="px-4 py-3 text-xs" style={{ color: '#6b6560' }}>{fmtDate(inv.createdAt)}</td>
                  <td className="px-4 py-3 text-xs" style={{ color: '#6b6560' }}>{fmtDate(inv.expiresAt)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button type="button" onClick={() => handleResend(inv)} disabled={busyId === inv._id}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-50"
                        style={{ background: '#1a1918', border: '1px solid rgba(237,232,224,0.08)', color: '#8a847c' }}>
                        <FaRedo className="text-xs" /> Resend
                      </button>
                      <button type="button" onClick={() => setRevoking(inv)} disabled={busyId === inv._id}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-50"
                        style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#f87171' }}>
                        <FaBan className="text-xs" /> Revoke
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {revoking && (
        <RevokeInviteModal invite={revoking} busy={busyId === revoking._id}
          onCancel={() => setRevoking(null)} onConfirm={handleRevoke} />
      )}
    </div>
  );
}

export default function ClubsList() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [search,      setSearch]      = useState('');
  const [status,      setStatus]      = useState('');
  const [plan,        setPlan]        = useState('');
  const [hasDiscount, setHasDiscount] = useState(false);
  const [risk,        setRisk]        = useState('');
  const [page,        setPage]        = useState(1);

  const params = { search, status, plan, page, limit: 20 };
  if (hasDiscount) params.hasDiscount = 'true';
  if (risk)        params.risk = risk;

  const { data, isPending } = useQuery({
    queryKey: ['sa-clubs', search, status, plan, hasDiscount, risk, page],
    queryFn:  () => saApi.get('/api/super-admin/clubs', { params }).then(r => r.data),
  });

  // Single source of truth for the Pro price is backend/config/plans.js — fetched here rather
  // than hardcoded, so the fallback below can never drift from it.
  const { data: plansData } = useQuery({
    queryKey: ['subscription-plans'],
    queryFn:  () => getPlans().then(r => r.data.plans),
    staleTime: 1000 * 60 * 10,
  });
  const proPrice = plansData?.pro?.price ?? 0;

  // colorScheme:'dark' makes the native option list render dark (no jarring blue/white highlight).
  const selectStyle = {
    background: '#1a1918', borderColor: 'rgba(237,232,224,0.08)', colorScheme: 'dark',
  };

  const clubs = data?.clubs || [];
  // Hide the Trial End column entirely when no club in the result set has a trial date.
  const showTrialEnd = clubs.some(c => (c.status === 'trial' || c.status === 'trialing') && c.trialEndsAt);
  const colCount = showTrialEnd ? 8 : 7;
  const headers = ['Club', 'Owner', 'Plan', 'Status', 'Members', 'Risk', ...(showTrialEnd ? ['Trial End'] : []), 'Created'];

  return (
    <div className="space-y-5">
      <div className="flex justify-between items-start gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#ede8e0]">Clubs</h1>
          <p className="text-sm mt-1" style={{ color: '#8a847c' }}>
            {data?.total ?? '…'} {risk ? (risk === 'none' ? 'clubs with no issues' : `${risk}-risk clubs`) : 'total clubs'}
          </p>
        </div>
        <button onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-colors flex-shrink-0"
          style={{ background: '#c8102e' }}>
          <FaPlus /> Add Club
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search name, slug, email…"
          className="border rounded-xl px-4 py-2.5 text-sm outline-none text-[#ede8e0] placeholder-[#6b6560] w-64"
          style={{ background: '#1a1918', borderColor: 'rgba(237,232,224,0.08)' }} />

        <select value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}
          className="border rounded-xl px-4 py-2.5 text-sm outline-none text-[#ede8e0] cursor-pointer"
          style={selectStyle}>
          {STATUS_FILTER_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>

        <select value={plan} onChange={e => { setPlan(e.target.value); setPage(1); }}
          className="border rounded-xl px-4 py-2.5 text-sm outline-none text-[#ede8e0] cursor-pointer"
          style={selectStyle}>
          <option value="">All plans</option>
          <option value="free">Free</option>
          <option value="pro">Pro</option>
        </select>

        {/* Risk filter */}
        <select
          value={risk}
          onChange={e => { setRisk(e.target.value); setPage(1); }}
          className="border rounded-xl px-4 py-2.5 text-sm outline-none font-medium cursor-pointer"
          style={{
            colorScheme: 'dark',
            background:  risk === 'high' ? 'rgba(239,68,68,0.12)' : risk === 'medium' ? 'rgba(251,191,36,0.1)' : risk === 'low' ? 'rgba(107,114,128,0.12)' : '#1a1918',
            borderColor: risk === 'high' ? 'rgba(239,68,68,0.4)' : risk === 'medium' ? 'rgba(251,191,36,0.4)' : risk === 'low' ? 'rgba(107,114,128,0.4)' : 'rgba(237,232,224,0.08)',
            color:       risk === 'high' ? '#f87171' : risk === 'medium' ? '#fbbf24' : risk === 'low' ? '#8a847c' : '#8a847c',
          }}>
          {RISK_FILTER_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>

        <button
          onClick={() => { setHasDiscount(v => !v); setPage(1); }}
          className="flex items-center gap-2 border rounded-xl px-4 py-2.5 text-sm font-medium transition-colors"
          style={{
            background:   hasDiscount ? '#7c3aed20' : '#1e1d1b',
            borderColor:  hasDiscount ? '#7c3aed'   : 'rgba(237,232,224,0.08)',
            color:        hasDiscount ? '#a78bfa'   : '#8a847c',
          }}>
          <MdDiscount className="text-base" />
          Discounted
        </button>
      </div>

      {/* Pending club-owner invitations (collapsed unless there are any) */}
      <PendingInvitations />

      {/* Table */}
      <div className="rounded-xl border overflow-x-auto" style={{ borderColor: 'rgba(237,232,224,0.08)' }}>
        <table className="w-full text-sm min-w-[900px]">
          <thead>
            <tr style={{ background: '#1a1918', borderBottom: '1px solid rgba(237,232,224,0.08)' }}>
              {headers.map(h => (
                <th key={h} className="px-4 py-3 text-left font-medium" style={{ color: '#8a847c' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isPending ? (
              <tr><td colSpan={colCount} className="px-4 py-8 text-center" style={{ color: '#6b6560' }}>Loading…</td></tr>
            ) : clubs.length === 0 ? (
              <tr>
                <td colSpan={colCount} className="px-4 py-10 text-center" style={{ color: '#6b6560' }}>
                  {risk ? `No ${risk === 'none' ? 'risk-free' : risk + '-risk'} clubs found` : 'No clubs found'}
                </td>
              </tr>
            ) : clubs.map(club => {
              const s      = getStatusBadge(club.status);
              const isTrial = club.status === 'trial' || club.status === 'trialing';
              const isHighRisk = club.riskLevel === 'high';
              return (
                <tr key={club._id}
                  onClick={() => navigate(`/superadmin/clubs/${club._id}`)}
                  style={{
                    borderBottom: '1px solid rgba(237,232,224,0.08)',
                    borderLeft:   isHighRisk ? '3px solid rgba(239,68,68,0.5)' : '3px solid transparent',
                    background:   isHighRisk ? 'rgba(239,68,68,0.03)' : 'transparent',
                  }}
                  className="hover:bg-[#28261f] transition-colors cursor-pointer">
                  <td className="px-4 py-3">
                    <div className="font-medium text-[#ede8e0] flex items-center gap-1.5">
                      {isHighRisk && <FaExclamationTriangle className="text-red-400 flex-shrink-0 text-xs" />}
                      {club.name}
                    </div>
                    <div className="text-xs" style={{ color: '#6b6560' }}>{club.slug}.trainaxis.com</div>
                  </td>
                  <td className="px-4 py-3">
                    <div style={{ color: '#c5bfb5' }}>{club.ownerName}</div>
                    <div className="text-xs" style={{ color: '#6b6560' }}>{club.ownerEmail || <span style={{ color: '#f87171' }}>No email</span>}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs font-semibold capitalize" style={{ color: PLAN_COLORS[club.subscriptionPlan] || '#8a847c' }}>
                      {club.subscriptionPlan || '—'}
                    </span>
                    {club.subscriptionPlan === 'pro' && (
                      <div className="mt-0.5">
                        {club.activeDiscount && club.discountStatus === 'active' ? (
                          <>
                            <div className="text-xs line-through" style={{ color: '#6b6560' }}>
                              NPR {(club.basePrice ?? proPrice).toLocaleString()}/mo
                            </div>
                            <div className="text-xs font-semibold" style={{ color: '#4ade80' }}>
                              NPR {Number(club.finalMonthlyPrice ?? club.activeDiscount.finalMonthlyPrice).toLocaleString()}/mo
                            </div>
                          </>
                        ) : club.discountStatus === 'expired' ? (
                          <div className="text-xs" style={{ color: '#6b6560' }}>
                            NPR {(club.basePrice ?? proPrice).toLocaleString()}/mo
                            <span className="ml-1 text-yellow-600">disc. expired</span>
                          </div>
                        ) : (
                          <div className="text-xs" style={{ color: '#6b6560' }}>
                            NPR {(club.basePrice ?? proPrice).toLocaleString()}/mo
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 rounded-full text-xs font-semibold"
                      style={s.style}>
                      {s.label}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center" style={{ color: '#8a847c' }}>
                    {club.memberCounts?.students ?? 0}
                  </td>
                  <td className="px-4 py-3">
                    <RiskCell riskLevel={club.riskLevel} riskLabels={club.riskLabels} />
                  </td>
                  {showTrialEnd && (
                    <td className="px-4 py-3 text-xs" style={{ color: '#6b6560' }}>
                      {isTrial && club.trialEndsAt ? (
                        <span className="flex items-center gap-1">
                          {new Date(club.trialEndsAt).toLocaleDateString()}
                          <TrialDaysChip days={club.trialDaysLeft} />
                        </span>
                      ) : '—'}
                    </td>
                  )}
                  <td className="px-4 py-3 text-xs" style={{ color: '#6b6560' }}>
                    {new Date(club.createdAt).toLocaleDateString()}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {data?.pages > 1 && (
        <div className="flex justify-center gap-2">
          {Array.from({ length: data.pages }, (_, i) => i + 1).map(p => (
            <button key={p} onClick={() => setPage(p)}
              className="w-8 h-8 rounded-lg text-sm font-medium transition-colors"
              style={{ background: p === page ? '#c8102e' : '#28261f', color: p === page ? '#fff' : '#8a847c' }}>
              {p}
            </button>
          ))}
        </div>
      )}

      {showCreate && (
        <CreateClubModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); qc.invalidateQueries({ queryKey: ['sa-clubs'] }); }}
          onInvited={(opts) => {
            qc.invalidateQueries({ queryKey: ['sa-club-invitations'] });
            if (!opts?.keepOpen) setShowCreate(false);
          }}
        />
      )}
    </div>
  );
}
